import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import SignatureCanvas from 'react-signature-canvas';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Building2, Upload, User, Phone, Mail, MapPin, Briefcase, Save, FileText, CheckCircle, AlertCircle, Trash2, Eye, PenTool, X, Plus } from 'lucide-react';
import { toast } from 'sonner';

const ProfilEntreprise = ({ user }) => {
  const [profile, setProfile] = useState({
    nom: '',
    nom_entreprise: '',
    adresse: '',
    code_postal: '',
    ville: '',
    telephone: '',
    email: user.email,
    siret: '',
    gerant_genre: '',
    gerant_nom: '',
    gerant_prenom: '',
    gerant_email: '',
    gerant_telephone: '',
    gerant_signature: '',
    logo: '',
    tampon: '',
    activites: {
      randonnee_vnm: false,
      nombre_vnm: 0,
      engins_tractes: false,
      pnm: false,
      flyboard: false,
      location_bateau: false
    }
  });
  
  const signatureRef = useRef(null);
  const tamponCanvasRef = useRef(null);

  const generateTamponCanvas = () => {
    const canvas = document.createElement('canvas');
    canvas.width = 400;
    canvas.height = 200;
    const ctx = canvas.getContext('2d');
    ctx.strokeStyle = '#1e40af';
    ctx.lineWidth = 3;
    ctx.beginPath();
    ctx.roundRect(10, 10, 380, 180, 15);
    ctx.stroke();
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.roundRect(16, 16, 368, 168, 12);
    ctx.stroke();
    ctx.fillStyle = '#1e40af';
    ctx.font = 'bold 18px Arial';
    ctx.textAlign = 'center';
    ctx.fillText((profile.nom_entreprise || profile.nom || 'ENTREPRISE').toUpperCase(), 200, 55);
    ctx.beginPath(); ctx.moveTo(80, 65); ctx.lineTo(320, 65); ctx.stroke();
    ctx.font = '12px Arial';
    ctx.fillText(profile.adresse || '', 200, 85);
    ctx.fillText(`${profile.code_postal || ''} ${profile.ville || ''}`, 200, 102);
    if (profile.siret) { ctx.font = '11px Arial'; ctx.fillText(`SIRET: ${profile.siret}`, 200, 125); }
    if (profile.telephone) { ctx.font = '11px Arial'; ctx.fillText(`Tel: ${profile.telephone}`, 200, 145); }
    if (profile.email) { ctx.font = '10px Arial'; ctx.fillText(profile.email, 200, 165); }
    return canvas.toDataURL('image/png');
  };

  const GENRES = [
    { value: '', label: 'Sélectionner...' },
    { value: 'M.', label: 'Monsieur' },
    { value: 'Mme', label: 'Madame' }
  ];

  const [documents, setDocuments] = useState({
    agrement: null,
    assurance: null
  });

  const [stagiaires, setStagiaires] = useState([]);
  const [selectedStagiaireAgrement, setSelectedStagiaireAgrement] = useState('');

  // États pour les offres d'emploi
  const [offres, setOffres] = useState([]);
  const [showOffreDialog, setShowOffreDialog] = useState(false);
  const [showCandidaturesDialog, setShowCandidaturesDialog] = useState(false);
  const [selectedOffre, setSelectedOffre] = useState(null);
  const [candidatures, setCandidatures] = useState([]);
  const [savingOffre, setSavingOffre] = useState(false);
  const [newOffre, setNewOffre] = useState({
    titre: '',
    description: '',
    type_contrat: '',
    lieu: '',
    date_debut_souhaitee: ''
  });

  useEffect(() => {
    loadProfile();
    loadDocuments();
    loadStagiaires();
    loadOffres();
  }, []);

  const loadProfile = async () => {
    try {
      const response = await axios.get(`/entreprises/${user.user_id}`);
      if (response.data.profile) {
        const p = { ...profile, ...response.data.profile, activites: { ...profile.activites, ...(response.data.profile.activites || {}) } };
        // Charger aussi le tampon depuis l'API
        try {
          const tamponRes = await axios.get('/entreprise/tampon');
          if (tamponRes.data?.tampon) p.tampon = tamponRes.data.tampon;
          if (tamponRes.data?.siret) p.siret = tamponRes.data.siret;
        } catch (e) {}
        setProfile(p);
      }
    } catch (error) {
    }
  };

  const loadDocuments = async () => {
    try {
      const response = await axios.get(`/documents/entreprise/${user.user_id}`);
      const docs = {};
      response.data.forEach(doc => {
        docs[doc.type] = doc;
      });
      setDocuments(docs);
    } catch (error) {
    }
  };

  const loadStagiaires = async () => {
    try {
      const response = await axios.get('/stagiaires');
      setStagiaires(response.data);
      if (response.data.length > 0) {
        setSelectedStagiaireAgrement(response.data[0].user_id);
      }
    } catch (error) {
    }
  };

  const loadOffres = async () => {
    try {
      const response = await axios.get('/offres-emploi/entreprise');
      setOffres(response.data || []);
    } catch (error) {
    }
  };

  const loadCandidatures = async (offreId) => {
    try {
      const response = await axios.get(`/offres-emploi/${offreId}/candidatures`);
      setCandidatures(response.data || []);
    } catch (error) {
      setCandidatures([]);
    }
  };

  const handleCreateOffre = async () => {
    setSavingOffre(true);
    try {
      await axios.post('/offres-emploi', newOffre);
      toast.success('Offre publiée avec succès');
      setShowOffreDialog(false);
      setNewOffre({ titre: '', description: '', type_contrat: '', lieu: '', date_debut_souhaitee: '' });
      loadOffres();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la création');
    } finally {
      setSavingOffre(false);
    }
  };

  const handleDeleteOffre = async (offreId) => {
    if (!window.confirm('Supprimer cette offre d\'emploi ?')) return;
    try {
      await axios.delete(`/offres-emploi/${offreId}`);
      toast.success('Offre supprimée');
      loadOffres();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const handleUpdateCandidature = async (candidatureId, statut) => {
    try {
      await axios.put(`/candidatures/${candidatureId}/statut`, { statut });
      toast.success('Statut mis à jour');
      if (selectedOffre) {
        loadCandidatures(selectedOffre.offre_id);
      }
    } catch (error) {
      toast.error('Erreur lors de la mise à jour');
    }
  };

  const handleSave = async () => {
    try {
      await axios.put(`/entreprises/${user.user_id}/profile`, profile);
      // Sauvegarder aussi tampon et SIRET
      if (profile.tampon || profile.siret) {
        await axios.post('/entreprise/tampon', { tampon: profile.tampon || '', siret: profile.siret || '' });
      }
      toast.success('Profil mis à jour avec succès');
    } catch (error) {
      toast.error('Erreur lors de la mise à jour');
    }
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onloadend = () => {
      setProfile(prev => ({ ...prev, logo: reader.result }));
      toast.success('Logo chargé - N\'oubliez pas d\'enregistrer');
    };
    reader.readAsDataURL(file);
  };

  const handleDocumentUpload = async (e, type) => {
    const file = e.target.files[0];
    if (!file) return;

    // Vérifier le type de fichier
    if (!file.type.includes('pdf') && !file.type.includes('image')) {
      toast.error('Format accepté : PDF ou Image');
      return;
    }

    // Vérifier la taille (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Fichier trop volumineux (max 5MB)');
      return;
    }

    const reader = new FileReader();
    reader.onloadend = async () => {
      try {
        const docData = {
          type,
          filename: file.name,
          content: reader.result,
          mime_type: file.type,
          stagiaire_id: type === 'agrement' ? selectedStagiaireAgrement : null
        };

        await axios.post(`/documents/entreprise/${user.user_id}`, docData);
        toast.success(`Document "${type === 'agrement' ? 'Agrément' : 'Attestation d\'assurance'}" uploadé avec succès`);
        loadDocuments();
      } catch (error) {
        toast.error('Erreur lors de l\'upload');
      }
    };
    reader.readAsDataURL(file);
  };

  const handleDeleteDocument = async (type) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer ce document ?')) return;

    try {
      await axios.delete(`/documents/entreprise/${user.user_id}/${type}`);
      toast.success('Document supprimé');
      setDocuments(prev => ({ ...prev, [type]: null }));
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const updateActivite = (key, value) => {
    setProfile(prev => ({
      ...prev,
      activites: {
        ...prev.activites,
        [key]: value
      }
    }));
  };

  const getSelectedStagiaireName = () => {
    const stag = stagiaires.find(s => s.user_id === selectedStagiaireAgrement);
    return stag ? `${stag.user_info?.prenom} ${stag.user_info?.nom}` : '';
  };

  return (
    <div className="space-y-6" data-testid="profil-entreprise-page">
      <div>
        <h1 className="text-4xl font-display font-bold text-[#0F172A] mb-2">Profil Entreprise</h1>
        <p className="text-slate-600">Gérez les informations de votre entreprise</p>
      </div>

      {/* Logo et Nom */}
      <div className="bg-white border border-slate-200 rounded-lg p-6 shadow-sm">
        <div className="flex items-start space-x-6">
          <div className="w-32 h-32 bg-slate-100 border-2 border-slate-200 rounded-lg overflow-hidden flex-shrink-0">
            {profile.logo ? (
              <img src={profile.logo} alt="Logo" className="w-full h-full object-contain p-2" />
            ) : (
              <div className="w-full h-full flex items-center justify-center">
                <Building2 className="h-16 w-16 text-slate-400" />
              </div>
            )}
          </div>
          <div className="flex-1 space-y-4">
            <div className="space-y-2">
              <Label htmlFor="nom" className="flex items-center gap-2">
                <Building2 className="h-4 w-4 text-[#8DC63F]" />
                Nom de l'Entreprise
              </Label>
              <Input 
                id="nom" 
                value={profile.nom} 
                onChange={(e) => setProfile({ ...profile, nom: e.target.value })}
                placeholder="Nom de votre entreprise"
                className="text-lg font-semibold"
              />
            </div>
            <label className="cursor-pointer inline-block">
              <input type="file" accept="image/*" onChange={handleLogoUpload} className="hidden" />
              <Button type="button" variant="outline" className="pointer-events-none">
                <Upload className="h-4 w-4 mr-2" />
                Changer le logo
              </Button>
            </label>
          </div>
        </div>
      </div>

      {/* Coordonnées Entreprise */}
      <div className="bg-white border border-slate-200 rounded-lg p-6 shadow-sm">
        <h2 className="text-xl font-display font-semibold text-[#0F172A] mb-4 flex items-center gap-2">
          <MapPin className="h-5 w-5 text-[#8DC63F]" />
          Coordonnées de l'Entreprise
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="space-y-2 md:col-span-3">
            <Label htmlFor="adresse">Adresse</Label>
            <Input 
              id="adresse" 
              value={profile.adresse} 
              onChange={(e) => setProfile({ ...profile, adresse: e.target.value })}
              placeholder="Numéro et nom de rue"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="code_postal">Code Postal</Label>
            <Input 
              id="code_postal" 
              value={profile.code_postal || ''} 
              onChange={(e) => setProfile({ ...profile, code_postal: e.target.value })}
              placeholder="17110"
              maxLength={5}
            />
          </div>
          <div className="space-y-2 md:col-span-2">
            <Label htmlFor="ville">Ville</Label>
            <Input 
              id="ville" 
              value={profile.ville || ''} 
              onChange={(e) => setProfile({ ...profile, ville: e.target.value })}
              placeholder="Saint-Georges-de-Didonne"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="telephone" className="flex items-center gap-2">
              <Phone className="h-4 w-4 text-slate-400" />
              Téléphone
            </Label>
            <Input 
              id="telephone" 
              value={profile.telephone} 
              onChange={(e) => setProfile({ ...profile, telephone: e.target.value })}
              placeholder="06 XX XX XX XX"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="siret" className="flex items-center gap-2">
              <Building2 className="h-4 w-4 text-slate-400" />
              SIRET
            </Label>
            <Input 
              id="siret" 
              value={profile.siret || ''} 
              onChange={(e) => setProfile({ ...profile, siret: e.target.value })}
              placeholder="123 456 789 00012"
              data-testid="siret-input"
            />
          </div>
          <div className="space-y-2 md:col-span-2">
            <Label htmlFor="email" className="flex items-center gap-2">
              <Mail className="h-4 w-4 text-slate-400" />
              Email
            </Label>
            <Input 
              id="email" 
              type="email" 
              value={profile.email} 
              disabled 
              className="bg-slate-50"
            />
          </div>
        </div>
      </div>

      {/* Informations Gérant */}
      <div className="bg-white border border-slate-200 rounded-lg p-6 shadow-sm">
        <h2 className="text-xl font-display font-semibold text-[#0F172A] mb-4 flex items-center gap-2">
          <User className="h-5 w-5 text-[#8DC63F]" />
          Gérant / Responsable
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="space-y-2">
            <Label htmlFor="gerant_genre">Genre</Label>
            <select
              id="gerant_genre"
              value={profile.gerant_genre || ''}
              onChange={(e) => setProfile({ ...profile, gerant_genre: e.target.value })}
              className="w-full h-10 px-3 text-sm border border-slate-200 rounded-md"
            >
              {GENRES.map(g => (
                <option key={g.value} value={g.value}>{g.label}</option>
              ))}
            </select>
          </div>
          <div className="space-y-2">
            <Label htmlFor="gerant_prenom">Prénom</Label>
            <Input 
              id="gerant_prenom" 
              value={profile.gerant_prenom || ''} 
              onChange={(e) => setProfile({ ...profile, gerant_prenom: e.target.value })}
              placeholder="Prénom"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="gerant_nom">Nom</Label>
            <Input 
              id="gerant_nom" 
              value={profile.gerant_nom} 
              onChange={(e) => setProfile({ ...profile, gerant_nom: e.target.value })}
              placeholder="Nom"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="gerant_telephone">Téléphone</Label>
            <Input 
              id="gerant_telephone" 
              value={profile.gerant_telephone} 
              onChange={(e) => setProfile({ ...profile, gerant_telephone: e.target.value })}
              placeholder="06 XX XX XX XX"
            />
          </div>
          <div className="space-y-2 md:col-span-2">
            <Label htmlFor="gerant_email">Email Personnel</Label>
            <Input 
              id="gerant_email" 
              type="email" 
              value={profile.gerant_email} 
              onChange={(e) => setProfile({ ...profile, gerant_email: e.target.value })}
              placeholder="email@personnel.com"
            />
          </div>
        </div>
        
        {/* Signature du Gérant */}
        <div className="mt-6 pt-6 border-t border-slate-200">
          <h3 className="text-lg font-semibold text-[#0F172A] mb-3 flex items-center gap-2">
            <PenTool className="h-5 w-5 text-[#8DC63F]" />
            Signature du Gérant
          </h3>
          <p className="text-sm text-slate-600 mb-4">
            Cette signature sera utilisée pour la validation des devis et documents officiels.
          </p>
          
          {profile.gerant_signature ? (
            <div className="space-y-3">
              <div className="p-4 border-2 border-green-200 rounded-lg bg-green-50">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-green-700 flex items-center gap-2">
                    <CheckCircle className="h-4 w-4" />
                    Signature enregistrée
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setProfile({ ...profile, gerant_signature: '' })}
                    className="text-red-600 hover:text-red-700 hover:bg-red-50"
                    data-testid="delete-signature-btn"
                  >
                    <Trash2 className="h-4 w-4 mr-1" />
                    Supprimer
                  </Button>
                </div>
                <div className="bg-white rounded border border-green-200 p-2">
                  <img 
                    src={profile.gerant_signature} 
                    alt="Signature du gérant" 
                    className="max-h-24 mx-auto"
                    data-testid="gerant-signature-preview"
                  />
                </div>
              </div>
            </div>
          ) : (
            <div className="space-y-3">
              <div className="border-2 border-dashed border-slate-300 rounded-lg p-4 bg-slate-50">
                <Label className="text-sm font-medium mb-2 block">Dessinez votre signature ci-dessous :</Label>
                <div className="border-2 border-slate-300 rounded-lg overflow-hidden bg-white">
                  <SignatureCanvas
                    ref={signatureRef}
                    canvasProps={{
                      className: 'w-full h-32 bg-white',
                      style: { touchAction: 'none' }
                    }}
                    penColor="#333"
                  />
                </div>
                <div className="flex gap-2 mt-3">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => signatureRef.current?.clear()}
                    data-testid="clear-signature-btn"
                  >
                    <X className="h-4 w-4 mr-1" />
                    Effacer
                  </Button>
                  <Button
                    size="sm"
                    className="bg-[#8DC63F] hover:bg-[#7AB62F]"
                    onClick={() => {
                      if (signatureRef.current && !signatureRef.current.isEmpty()) {
                        const signatureData = signatureRef.current.toDataURL();
                        setProfile({ ...profile, gerant_signature: signatureData });
                        toast.success('Signature enregistrée. N\'oubliez pas de sauvegarder le profil.');
                      } else {
                        toast.error('Veuillez dessiner votre signature');
                      }
                    }}
                    data-testid="save-signature-btn"
                  >
                    <CheckCircle className="h-4 w-4 mr-1" />
                    Enregistrer la signature
                  </Button>
                </div>
              </div>
            </div>
          )}
        </div>
        
        {/* Tampon de l'Entreprise */}
        <div className="mt-6 pt-6 border-t border-slate-200">
          <h3 className="text-lg font-semibold text-[#0F172A] mb-3 flex items-center gap-2">
            <Building2 className="h-5 w-5 text-blue-500" />
            Tampon de l'Entreprise
          </h3>
          <p className="text-sm text-slate-600 mb-4">
            Ce tampon est généré automatiquement à partir des informations de votre entreprise. Il sera apposé avec votre signature sur les conventions.
          </p>
          
          {profile.tampon ? (
            <div className="space-y-3">
              <div className="p-4 border-2 border-blue-200 rounded-lg bg-blue-50">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-blue-700 flex items-center gap-2">
                    <CheckCircle className="h-4 w-4" />
                    Tampon enregistré
                  </span>
                  <div className="flex gap-2">
                    <Button
                      variant="outline" size="sm"
                      onClick={() => {
                        const t = generateTamponCanvas();
                        setProfile({ ...profile, tampon: t });
                        toast.success('Tampon régénéré ! Sauvegardez pour appliquer.');
                      }}
                      className="text-blue-600 hover:bg-blue-50"
                    >
                      Régénérer
                    </Button>
                    <label className="cursor-pointer">
                      <input type="file" accept="image/*" className="hidden" onChange={(e) => {
                        const file = e.target.files[0];
                        if (file) {
                          const reader = new FileReader();
                          reader.onload = (ev) => {
                            setProfile({ ...profile, tampon: ev.target.result });
                            toast.success('Tampon uploadé ! Sauvegardez pour appliquer.');
                          };
                          reader.readAsDataURL(file);
                        }
                      }} />
                      <span className="inline-flex items-center px-3 py-1.5 border border-slate-300 rounded-md text-sm font-medium text-slate-700 hover:bg-slate-50">
                        <Upload className="h-4 w-4 mr-1" /> Remplacer
                      </span>
                    </label>
                    <Button
                      variant="outline" size="sm"
                      onClick={() => setProfile({ ...profile, tampon: '' })}
                      className="text-red-600 hover:text-red-700 hover:bg-red-50"
                      data-testid="delete-tampon-btn"
                    >
                      <Trash2 className="h-4 w-4 mr-1" /> Supprimer
                    </Button>
                  </div>
                </div>
                <div className="bg-white rounded border border-blue-200 p-2 text-center">
                  <img src={profile.tampon} alt="Tampon entreprise" className="max-h-32 mx-auto" data-testid="tampon-preview" />
                </div>
              </div>
            </div>
          ) : (
            <div className="space-y-3 flex gap-3 items-start">
              <Button
                size="sm"
                className="bg-blue-500 hover:bg-blue-600"
                onClick={() => {
                  const t = generateTamponCanvas();
                  setProfile({ ...profile, tampon: t });
                  toast.success('Tampon généré ! Sauvegardez pour appliquer.');
                }}
                data-testid="generate-tampon-btn"
              >
                <Building2 className="h-4 w-4 mr-1" />
                Générer le tampon
              </Button>
              <label className="cursor-pointer">
                <input type="file" accept="image/*" className="hidden" onChange={(e) => {
                  const file = e.target.files[0];
                  if (file) {
                    const reader = new FileReader();
                    reader.onload = (ev) => {
                      setProfile({ ...profile, tampon: ev.target.result });
                      toast.success('Tampon uploadé ! Sauvegardez pour appliquer.');
                    };
                    reader.readAsDataURL(file);
                  }
                }} />
                <span className="inline-flex items-center px-3 py-2 border border-slate-300 rounded-md text-sm font-medium text-slate-700 hover:bg-slate-50 cursor-pointer">
                  <Upload className="h-4 w-4 mr-1" /> Uploader un tampon
                </span>
              </label>
              <p className="text-xs text-slate-400 mt-2">Remplissez d'abord le nom, adresse, SIRET et téléphone ci-dessus.</p>
            </div>
          )}
        </div>
      </div>

      {/* Documents Obligatoires */}
      <div className="bg-white border border-slate-200 rounded-lg p-6 shadow-sm border-l-4 border-l-orange-500">
        <h2 className="text-xl font-display font-semibold text-[#0F172A] mb-2 flex items-center gap-2">
          <FileText className="h-5 w-5 text-orange-500" />
          Documents Obligatoires
        </h2>
        <p className="text-sm text-slate-600 mb-6">Veuillez télécharger les documents requis (PDF ou Image, max 5MB)</p>

        <div className="space-y-6">
          {/* Agrément */}
          <div className="p-4 border border-slate-200 rounded-lg bg-slate-50/50">
            <div className="flex items-start justify-between mb-3">
              <div>
                <h3 className="font-semibold text-[#0F172A] flex items-center gap-2">
                  {documents.agrement ? (
                    <CheckCircle className="h-5 w-5 text-green-500" />
                  ) : (
                    <AlertCircle className="h-5 w-5 text-orange-500" />
                  )}
                  Agrément
                </h3>
                <p className="text-sm text-slate-600">Document faisant apparaître le nom du stagiaire</p>
              </div>
              {documents.agrement && (
                <div className="flex items-center gap-2">
                  <span className="text-xs text-green-600 bg-green-100 px-2 py-1 rounded-full">Uploadé</span>
                </div>
              )}
            </div>

            {/* Sélection du stagiaire pour l'agrément */}
            <div className="mb-3">
              <Label className="text-sm text-slate-600">Stagiaire concerné par l'agrément</Label>
              <select
                value={selectedStagiaireAgrement}
                onChange={(e) => setSelectedStagiaireAgrement(e.target.value)}
                className="w-full h-10 px-3 py-2 mt-1 text-sm border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-[#8DC63F]"
              >
                {stagiaires.map((s) => (
                  <option key={s.user_id} value={s.user_id}>
                    {s.user_info?.prenom} {s.user_info?.nom}
                  </option>
                ))}
              </select>
            </div>

            {documents.agrement ? (
              <div className="flex items-center justify-between p-3 bg-white rounded-lg border border-slate-200">
                <div className="flex items-center gap-3">
                  <FileText className="h-8 w-8 text-[#8DC63F]" />
                  <div>
                    <p className="font-medium text-[#0F172A]">{documents.agrement.filename}</p>
                    <p className="text-xs text-slate-500">
                      Uploadé le {new Date(documents.agrement.date).toLocaleDateString('fr-FR')}
                      {documents.agrement.stagiaire_nom && ` - Pour: ${documents.agrement.stagiaire_nom}`}
                    </p>
                  </div>
                </div>
                <div className="flex gap-2">
                  {documents.agrement.content && (
                    <a 
                      href={documents.agrement.content} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                      title="Voir le document"
                    >
                      <Eye className="h-4 w-4" />
                    </a>
                  )}
                  <button 
                    onClick={() => handleDeleteDocument('agrement')}
                    className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                    title="Supprimer"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>
            ) : (
              <label className="cursor-pointer block">
                <input 
                  type="file" 
                  accept=".pdf,image/*" 
                  onChange={(e) => handleDocumentUpload(e, 'agrement')} 
                  className="hidden" 
                />
                <div className="border-2 border-dashed border-slate-300 rounded-lg p-6 text-center hover:border-[#8DC63F] hover:bg-[#8DC63F]/5 transition-colors">
                  <Upload className="h-8 w-8 text-slate-400 mx-auto mb-2" />
                  <p className="text-sm font-medium text-slate-700">Cliquez pour uploader l'agrément</p>
                  <p className="text-xs text-slate-500 mt-1">PDF ou Image (max 5MB)</p>
                </div>
              </label>
            )}
          </div>

          {/* Attestation d'assurance */}
          <div className="p-4 border border-slate-200 rounded-lg bg-slate-50/50">
            <div className="flex items-start justify-between mb-3">
              <div>
                <h3 className="font-semibold text-[#0F172A] flex items-center gap-2">
                  {documents.assurance ? (
                    <CheckCircle className="h-5 w-5 text-green-500" />
                  ) : (
                    <AlertCircle className="h-5 w-5 text-orange-500" />
                  )}
                  Attestation d'Assurance
                </h3>
                <p className="text-sm text-slate-600">Votre attestation d'assurance professionnelle</p>
              </div>
              {documents.assurance && (
                <div className="flex items-center gap-2">
                  <span className="text-xs text-green-600 bg-green-100 px-2 py-1 rounded-full">Uploadé</span>
                </div>
              )}
            </div>

            {documents.assurance ? (
              <div className="flex items-center justify-between p-3 bg-white rounded-lg border border-slate-200">
                <div className="flex items-center gap-3">
                  <FileText className="h-8 w-8 text-[#8DC63F]" />
                  <div>
                    <p className="font-medium text-[#0F172A]">{documents.assurance.filename}</p>
                    <p className="text-xs text-slate-500">
                      Uploadé le {new Date(documents.assurance.date).toLocaleDateString('fr-FR')}
                    </p>
                  </div>
                </div>
                <div className="flex gap-2">
                  {documents.assurance.content && (
                    <a 
                      href={documents.assurance.content} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                      title="Voir le document"
                    >
                      <Eye className="h-4 w-4" />
                    </a>
                  )}
                  <button 
                    onClick={() => handleDeleteDocument('assurance')}
                    className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                    title="Supprimer"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>
            ) : (
              <label className="cursor-pointer block">
                <input 
                  type="file" 
                  accept=".pdf,image/*" 
                  onChange={(e) => handleDocumentUpload(e, 'assurance')} 
                  className="hidden" 
                />
                <div className="border-2 border-dashed border-slate-300 rounded-lg p-6 text-center hover:border-[#8DC63F] hover:bg-[#8DC63F]/5 transition-colors">
                  <Upload className="h-8 w-8 text-slate-400 mx-auto mb-2" />
                  <p className="text-sm font-medium text-slate-700">Cliquez pour uploader l'attestation d'assurance</p>
                  <p className="text-xs text-slate-500 mt-1">PDF ou Image (max 5MB)</p>
                </div>
              </label>
            )}
          </div>
        </div>
      </div>

      {/* Activités */}
      <div className="bg-white border border-slate-200 rounded-lg p-6 shadow-sm">
        <h2 className="text-xl font-display font-semibold text-[#0F172A] mb-4 flex items-center gap-2">
          <Briefcase className="h-5 w-5 text-[#8DC63F]" />
          Activités Proposées
        </h2>
        <div className="space-y-4">
          {/* Randonnée VNM avec nombre */}
          <div className="p-4 border border-slate-200 rounded-lg bg-slate-50/50">
            <div className="flex items-center justify-between">
              <label className="flex items-center space-x-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={profile.activites.randonnee_vnm}
                  onChange={(e) => updateActivite('randonnee_vnm', e.target.checked)}
                  className="w-5 h-5 text-[#8DC63F] border-slate-300 rounded focus:ring-[#8DC63F]"
                />
                <span className="font-medium text-[#0F172A]">Randonnée VNM</span>
              </label>
              {profile.activites.randonnee_vnm && (
                <div className="flex items-center gap-2">
                  <Label className="text-sm text-slate-600">Nombre de VNM :</Label>
                  <Input
                    type="number"
                    min="0"
                    value={profile.activites.nombre_vnm}
                    onChange={(e) => updateActivite('nombre_vnm', parseInt(e.target.value) || 0)}
                    className="w-20 h-8 text-center"
                  />
                </div>
              )}
            </div>
          </div>

          {/* Autres activités */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[
              { key: 'engins_tractes', label: 'Engins tractés' },
              { key: 'pnm', label: 'PNM' },
              { key: 'flyboard', label: 'Flyboard' },
              { key: 'location_bateau', label: 'Location bateau' }
            ].map((activite) => (
              <label 
                key={activite.key} 
                className={`flex items-center space-x-3 p-4 border rounded-lg cursor-pointer transition-all ${
                  profile.activites[activite.key] 
                    ? 'border-[#8DC63F] bg-[#8DC63F]/5' 
                    : 'border-slate-200 hover:border-slate-300'
                }`}
              >
                <input
                  type="checkbox"
                  checked={profile.activites[activite.key] || false}
                  onChange={(e) => updateActivite(activite.key, e.target.checked)}
                  className="w-5 h-5 text-[#8DC63F] border-slate-300 rounded focus:ring-[#8DC63F]"
                />
                <span className="font-medium text-[#0F172A]">{activite.label}</span>
              </label>
            ))}
          </div>
        </div>
      </div>

      {/* Offres d'emploi */}
      <div className="bg-white border border-slate-200 rounded-lg p-6 shadow-sm border-l-4 border-l-blue-500">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-display font-semibold text-[#0F172A] flex items-center gap-2">
            <Briefcase className="h-5 w-5 text-blue-500" />
            Offres d'emploi
          </h2>
          <Button 
            onClick={() => setShowOffreDialog(true)}
            className="bg-blue-600 hover:bg-blue-700"
          >
            <Plus className="h-4 w-4 mr-2" />
            Nouvelle offre
          </Button>
        </div>
        <p className="text-sm text-slate-600 mb-4">
          Publiez des offres d'emploi pour recruter des moniteurs VNM diplômés.
        </p>

        {offres.length === 0 ? (
          <div className="text-center py-8 border-2 border-dashed border-slate-200 rounded-lg">
            <Briefcase className="h-12 w-12 text-slate-300 mx-auto mb-3" />
            <p className="text-slate-500">Aucune offre d'emploi publiée</p>
            <p className="text-sm text-slate-400 mt-1">Cliquez sur "Nouvelle offre" pour commencer</p>
          </div>
        ) : (
          <div className="space-y-3">
            {offres.map(offre => (
              <div key={offre.offre_id} className="p-4 border border-slate-200 rounded-lg hover:shadow-md transition-shadow">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <h3 className="font-semibold text-slate-900">{offre.titre}</h3>
                      <span className={`px-2 py-0.5 text-xs rounded-full ${
                        offre.statut === 'active' ? 'bg-green-100 text-green-700' :
                        offre.statut === 'pourvue' ? 'bg-blue-100 text-blue-700' :
                        'bg-slate-100 text-slate-600'
                      }`}>
                        {offre.statut === 'active' ? 'Active' : 
                         offre.statut === 'pourvue' ? 'Pourvue' : 'Fermée'}
                      </span>
                    </div>
                    <div className="flex items-center gap-4 mt-1 text-sm text-slate-600">
                      <span className="flex items-center gap-1">
                        <MapPin className="h-3 w-3" />
                        {offre.lieu}
                      </span>
                      <span className="flex items-center gap-1">
                        <FileText className="h-3 w-3" />
                        {offre.type_contrat}
                      </span>
                      <span className="flex items-center gap-1">
                        <User className="h-3 w-3" />
                        {offre.nb_candidatures || 0} candidature(s)
                      </span>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => {
                        setSelectedOffre(offre);
                        setShowCandidaturesDialog(true);
                        loadCandidatures(offre.offre_id);
                      }}
                    >
                      <Eye className="h-4 w-4 mr-1" />
                      Voir
                    </Button>
                    <Button 
                      variant="outline" 
                      size="sm"
                      className="text-red-600 hover:bg-red-50"
                      onClick={() => handleDeleteOffre(offre.offre_id)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Bouton Enregistrer */}
      <div className="flex justify-end">
        <Button onClick={handleSave} className="bg-[#8DC63F] hover:bg-[#7db336] text-white px-8">
          <Save className="h-4 w-4 mr-2" />
          Enregistrer les modifications
        </Button>
      </div>

      {/* Dialog Nouvelle Offre */}
      {showOffreDialog && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-lg mx-4">
            <h3 className="text-lg font-semibold text-slate-900 mb-4">Nouvelle offre d'emploi</h3>
            <div className="space-y-4">
              <div>
                <Label>Titre du poste *</Label>
                <Input
                  value={newOffre.titre}
                  onChange={(e) => setNewOffre({...newOffre, titre: e.target.value})}
                  placeholder="Ex: Moniteur VNM saison été"
                />
              </div>
              <div>
                <Label>Description *</Label>
                <textarea
                  value={newOffre.description}
                  onChange={(e) => setNewOffre({...newOffre, description: e.target.value})}
                  placeholder="Décrivez le poste, les missions, les conditions..."
                  className="w-full min-h-[100px] px-3 py-2 text-sm border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Type de contrat *</Label>
                  <select
                    value={newOffre.type_contrat}
                    onChange={(e) => setNewOffre({...newOffre, type_contrat: e.target.value})}
                    className="w-full h-10 px-3 text-sm border border-slate-200 rounded-md"
                  >
                    <option value="">Sélectionner...</option>
                    <option value="CDI">CDI</option>
                    <option value="CDD">CDD</option>
                    <option value="Saisonnier">Saisonnier</option>
                    <option value="Intérim">Intérim</option>
                    <option value="Autre">Autre</option>
                  </select>
                </div>
                <div>
                  <Label>Lieu *</Label>
                  <Input
                    value={newOffre.lieu}
                    onChange={(e) => setNewOffre({...newOffre, lieu: e.target.value})}
                    placeholder="Ville"
                  />
                </div>
              </div>
              <div>
                <Label>Date de début souhaitée</Label>
                <Input
                  type="date"
                  value={newOffre.date_debut_souhaitee}
                  onChange={(e) => setNewOffre({...newOffre, date_debut_souhaitee: e.target.value})}
                />
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-6">
              <Button variant="outline" onClick={() => setShowOffreDialog(false)}>
                Annuler
              </Button>
              <Button 
                onClick={handleCreateOffre}
                disabled={savingOffre || !newOffre.titre || !newOffre.description || !newOffre.type_contrat || !newOffre.lieu}
                className="bg-blue-600 hover:bg-blue-700"
              >
                {savingOffre ? 'Création...' : 'Publier l\'offre'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Dialog Candidatures */}
      {showCandidaturesDialog && selectedOffre && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-2xl mx-4 max-h-[80vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-slate-900">
                Candidatures - {selectedOffre.titre}
              </h3>
              <button onClick={() => setShowCandidaturesDialog(false)} className="text-slate-400 hover:text-slate-600">
                <X className="h-5 w-5" />
              </button>
            </div>
            
            {candidatures.length === 0 ? (
              <div className="text-center py-8">
                <User className="h-12 w-12 text-slate-300 mx-auto mb-3" />
                <p className="text-slate-500">Aucune candidature reçue</p>
              </div>
            ) : (
              <div className="space-y-4">
                {candidatures.map(cand => (
                  <div key={cand.candidature_id} className="p-4 border border-slate-200 rounded-lg">
                    <div className="flex items-start justify-between">
                      <div>
                        <h4 className="font-semibold text-slate-900">
                          {cand.moniteur_prenom} {cand.moniteur_nom}
                        </h4>
                        <div className="flex items-center gap-4 mt-1 text-sm text-slate-600">
                          <span>{cand.moniteur_email}</span>
                          {cand.moniteur_telephone && <span>{cand.moniteur_telephone}</span>}
                          {cand.moniteur_ville && <span>{cand.moniteur_ville}</span>}
                        </div>
                        <p className="text-xs text-slate-500 mt-1">
                          Diplômé en {cand.moniteur_annee_diplome || 'N/A'}
                        </p>
                      </div>
                      <span className={`px-2 py-1 text-xs rounded-full ${
                        cand.statut === 'acceptee' ? 'bg-green-100 text-green-700' :
                        cand.statut === 'refusee' ? 'bg-red-100 text-red-700' :
                        cand.statut === 'entretien' ? 'bg-blue-100 text-blue-700' :
                        'bg-amber-100 text-amber-700'
                      }`}>
                        {cand.statut === 'acceptee' ? 'Acceptée' :
                         cand.statut === 'refusee' ? 'Refusée' :
                         cand.statut === 'entretien' ? 'Entretien' : 'En attente'}
                      </span>
                    </div>
                    {cand.commentaire && (
                      <div className="mt-3 p-3 bg-slate-50 rounded text-sm text-slate-700">
                        <p className="font-medium text-slate-600 mb-1">Message :</p>
                        {cand.commentaire}
                      </div>
                    )}
                    {cand.moniteur_cv && (
                      <Button 
                        variant="outline" 
                        size="sm" 
                        className="mt-3"
                        onClick={() => {
                          const link = document.createElement('a');
                          link.href = cand.moniteur_cv;
                          link.download = `CV_${cand.moniteur_nom}_${cand.moniteur_prenom}.pdf`;
                          link.click();
                        }}
                      >
                        <FileText className="h-4 w-4 mr-1" />
                        Télécharger CV
                      </Button>
                    )}
                    <div className="flex gap-2 mt-3">
                      <Button 
                        size="sm" 
                        variant="outline"
                        className="text-blue-600"
                        onClick={() => handleUpdateCandidature(cand.candidature_id, 'entretien')}
                      >
                        Entretien
                      </Button>
                      <Button 
                        size="sm" 
                        variant="outline"
                        className="text-green-600"
                        onClick={() => handleUpdateCandidature(cand.candidature_id, 'acceptee')}
                      >
                        Accepter
                      </Button>
                      <Button 
                        size="sm" 
                        variant="outline"
                        className="text-red-600"
                        onClick={() => handleUpdateCandidature(cand.candidature_id, 'refusee')}
                      >
                        Refuser
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ProfilEntreprise;
